import {
  ButtonLink,
  ContentWrapper,
  DownArrow,
  ErrorContainer,
  IconContainer,
  Label,
  TextMedium,
  UpArrow,
} from './ExpandableLink.style';
import { CALL_STATE, NetworkState, NetworkSuccess } from '../../types';
import { CookieInformationLabels, DeviceStorage } from '@usercentrics/cmp-browser-sdk';
import { Fragment, FunctionalComponent, h } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { BodyText } from '../List';
import CookieInformation from '../CookieInformation/CookieInformation';
import { LoadingText } from '../LoadingText/LoadingText';
import { fetchUrl } from '../StorageInfo/helpers';
import { useSettingsContext } from '../../contexts/SettingsContext';

type Props = {
  ariaLabel: string;
  deviceStorage: DeviceStorage | null;
  disclosureUrl: string | null;
  onToggleExpandable?: () => void;
  labels: CookieInformationLabels;
  isNonIabVendor: boolean | null;
};

const ExpandableLink: FunctionalComponent<Props> = ({
  ariaLabel,
  deviceStorage: fallbackDeviceStorage,
  disclosureUrl,
  labels,
  isNonIabVendor,
  onToggleExpandable,
}) => {
  const [toggleContent, setToggleContent] = useState(false);
  const [deviceStorageNetwork, setDeviceStorageNetwork] = useState<NetworkState<DeviceStorage>>({
    state: CALL_STATE.NOT_CALLED,
  });
  const [shouldRetry, setShouldRetry] = useState(false);
  const { settings } = useSettingsContext();
  const [disclosuresLanguage, setDisclosuresLanguage] = useState(settings.ui.language.selected);
  const { selected } = settings.ui.language;

  const { state: callState } = deviceStorageNetwork;

  const onShowExpandableToggle = (): void => {
    setToggleContent(!toggleContent);

    if (callState === CALL_STATE.NOT_CALLED) {
      setDeviceStorageNetwork({ state: CALL_STATE.LOADING });
    }
  };

  // Update disclosure language by refetching or assigning translated fallback
  useEffect(() => {
    if (disclosuresLanguage !== selected && callState === CALL_STATE.LOADED) {
      setDisclosuresLanguage(selected);
      setDeviceStorageNetwork({ state: CALL_STATE.LOADING });
    }
  }, [callState, disclosuresLanguage, selected]);

  useEffect(() => {
    if (callState !== CALL_STATE.NOT_CALLED && onToggleExpandable) {
      onToggleExpandable();
    }
  }, [onToggleExpandable, callState, toggleContent]);

  useEffect(() => {
    if (callState === CALL_STATE.LOADING || shouldRetry) {
      if (disclosureUrl && disclosureUrl !== '') {
        fetchUrl(disclosureUrl)
          .then((response) => response && response.json())
          .then((parsedResponse) => {
            setShouldRetry(false);

            if (parsedResponse?.disclosures) {
              const { disclosures } = parsedResponse;
              setDeviceStorageNetwork({ response: { disclosures }, state: CALL_STATE.LOADED });
            } else {
              setDeviceStorageNetwork({ state: CALL_STATE.ERROR });
            }
          })
          .catch(() => {
            setShouldRetry(false);
            setDeviceStorageNetwork({ state: CALL_STATE.ERROR });
          });
      } else if (fallbackDeviceStorage && fallbackDeviceStorage.disclosures.length > 0) {
        setDeviceStorageNetwork({
          response: { disclosures: fallbackDeviceStorage.disclosures },
          state: CALL_STATE.LOADED,
        });
      }
    }
  }, [callState, fallbackDeviceStorage, shouldRetry, disclosureUrl]);

  return (
    <Fragment>
      <ButtonLink
        data-testid="uc-expandable-link"
        onClick={onShowExpandableToggle}
        role="button"
        aria-label={ariaLabel}
        aria-expanded={toggleContent ? 'true' : 'false'}
      >
        <Label>{labels.storedInformation}</Label>
        <IconContainer>{!toggleContent ? <UpArrow /> : <DownArrow />}</IconContainer>
      </ButtonLink>

      {callState !== CALL_STATE.NOT_CALLED && toggleContent && (
        <ContentWrapper>
          {isNonIabVendor && <BodyText>{labels.storedInformationDescription}</BodyText>}
          {callState === CALL_STATE.LOADING && <LoadingText text={labels.loading} />}
          {callState === CALL_STATE.LOADED &&
            (deviceStorageNetwork as NetworkSuccess<DeviceStorage>).response.disclosures.map(
              (disclosure, index: number) => (
                <CookieInformation
                  labels={labels}
                  identifier={disclosure.identifier}
                  name={disclosure.name}
                  description={disclosure.description}
                  type={disclosure.type}
                  maxAgeSeconds={disclosure.maxAgeSeconds}
                  cookieRefresh={disclosure.cookieRefresh}
                  domain={disclosure.domain}
                  purposes={disclosure.purposes}
                  isLast={
                    index === (deviceStorageNetwork as NetworkSuccess<DeviceStorage>).response.disclosures.length - 1
                  }
                />
              ),
            )}
          {callState === CALL_STATE.ERROR && (
            <ErrorContainer>
              <TextMedium>{labels.error}</TextMedium>
              <ButtonLink onClick={() => setShouldRetry(true)} role="button" aria-label={labels.tryAgain}>
                <Label>{labels.tryAgain}</Label>
              </ButtonLink>
            </ErrorContainer>
          )}
        </ContentWrapper>
      )}
    </Fragment>
  );
};

export default ExpandableLink;
